/**
 * Session Tracking Helper for Analytics
 * Provides utilities for tracking session quality, engagement depth,
 * and user behavior within sessions.
 */

import logger from './logger';

/**
 * Session tracker class to maintain state across components
 */
class SessionTracker {
  constructor() {
    this.sessionStartTime = null;
    this.eventCount = 0;
    this.screensViewed = new Set();
    this.productsViewed = new Set();
    this.isFirstSession = false;
    this.pageLoadTimes = {};
    this.currentPageStartTime = null;
    this.currentPage = null;
  }

  /**
   * Initialize or reset session
   */
  startSession(isFirstSession = false) {
    this.sessionStartTime = Date.now();
    this.eventCount = 0;
    this.screensViewed = new Set();
    this.productsViewed = new Set();
    this.isFirstSession = isFirstSession;
    this.pageLoadTimes = {};
    this.currentPageStartTime = null;
    this.currentPage = null;
    logger.log('[SessionTracker] Session started');
  }

  /**
   * Increment event counter
   */
  incrementEvent() {
    this.eventCount++;
  }

  /**
   * Track a screen view
   * @param {string} screenName - Name of the screen
   */
  trackScreen(screenName) {
    if (screenName) {
      this.screensViewed.add(screenName);
    }
  }

  /**
   * Track a product view
   * @param {string} modelId - Vehicle model ID
   */
  trackProduct(modelId) {
    if (modelId) {
      this.productsViewed.add(modelId);
    }
  }

  /**
   * Start tracking time on a page
   * @param {string} pageName - Name of the page
   */
  startPageTracking(pageName) {
    // Save previous page time if exists
    if (this.currentPage && this.currentPageStartTime) {
      const timeSpent = Date.now() - this.currentPageStartTime;
      this.pageLoadTimes[this.currentPage] = timeSpent;
    }

    this.currentPage = pageName;
    this.currentPageStartTime = Date.now();
  }

  /**
   * Get time spent on current page
   * @returns {number} Time in seconds
   */
  getCurrentPageTimeSeconds() {
    if (!this.currentPageStartTime) return 0;
    return Math.round((Date.now() - this.currentPageStartTime) / 1000);
  }

  /**
   * Get time spent on a specific page
   * @param {string} pageName - Name of the page
   * @returns {number} Time in seconds
   */
  getPageTimeSeconds(pageName) {
    const timeMs = this.pageLoadTimes[pageName] || 0;
    return Math.round(timeMs / 1000);
  }

  /**
   * Get session duration
   * @returns {number} Duration in seconds
   */
  getSessionDurationSeconds() {
    if (!this.sessionStartTime) return 0;
    return Math.round((Date.now() - this.sessionStartTime) / 1000);
  }

  /**
   * Get session properties for analytics
   * @returns {Object} Session properties
   */
  getSessionProperties() {
    return {
      session_duration_seconds: this.getSessionDurationSeconds(),
      session_event_count: this.eventCount,
      session_screens_viewed: this.screensViewed.size,
      session_products_viewed: this.productsViewed.size,
      is_first_session: this.isFirstSession,
      session_type: this.inferSessionType(),
    };
  }

  /**
   * Get page-specific properties
   * @returns {Object} Page tracking properties
   */
  getPageProperties() {
    return {
      time_on_page_seconds: this.getCurrentPageTimeSeconds(),
      current_page: this.currentPage,
    };
  }

  /**
   * Infer session type based on behavior
   * @returns {string} Session type
   */
  inferSessionType() {
    const duration = this.getSessionDurationSeconds();
    const productsViewed = this.productsViewed.size;
    const eventCount = this.eventCount;

    // Quick check: Very short session with few events
    if (duration < 30 && eventCount < 5) {
      return 'quick_check';
    }

    // High intent: Multiple products viewed, longer session
    if (productsViewed >= 3 && duration >= 120) {
      return 'high_intent';
    }

    // Conversion: Moderate duration, focused activity
    if (eventCount >= 10 && productsViewed >= 1) {
      return 'conversion';
    }

    // Exploration: Longer session, browsing
    if (duration >= 60 || productsViewed >= 2) {
      return 'exploration';
    }

    return 'casual_browse';
  }

  /**
   * Check if user is showing high engagement
   * @returns {boolean} True if high engagement
   */
  isHighEngagement() {
    const duration = this.getSessionDurationSeconds();
    const productsViewed = this.productsViewed.size;
    const screensViewed = this.screensViewed.size;

    return duration >= 180 || productsViewed >= 3 || screensViewed >= 5;
  }

  /**
   * Reset session data
   */
  reset() {
    this.sessionStartTime = null;
    this.eventCount = 0;
    this.screensViewed.clear();
    this.productsViewed.clear();
    this.isFirstSession = false;
    this.pageLoadTimes = {};
    this.currentPageStartTime = null;
    this.currentPage = null;
    logger.log('[SessionTracker] Session reset');
  }
}

// Global singleton instance
let sessionTrackerInstance = null;

/**
 * Get the global session tracker instance
 * @returns {SessionTracker} Session tracker instance
 */
export const getSessionTracker = () => {
  if (!sessionTrackerInstance) {
    sessionTrackerInstance = new SessionTracker();
  }
  return sessionTrackerInstance;
};

/**
 * Get session context properties for event tracking
 * Convenience function that wraps getSessionTracker()
 * @returns {Object} Session properties
 */
export const getSessionContext = () => {
  const tracker = getSessionTracker();
  return tracker.getSessionProperties();
};

/**
 * Get page context properties for event tracking
 * @returns {Object} Page properties
 */
export const getPageContext = () => {
  const tracker = getSessionTracker();
  return tracker.getPageProperties();
};

/**
 * Track an event in the session
 * Call this for every PostHog event to maintain accurate counts
 */
export const trackSessionEvent = () => {
  const tracker = getSessionTracker();
  tracker.incrementEvent();
};

/**
 * Initialize session tracking
 * Call this when app starts or user logs in
 * @param {boolean} isFirstSession - Whether this is the user's first session
 */
export const initializeSession = (isFirstSession = false) => {
  const tracker = getSessionTracker();
  tracker.startSession(isFirstSession);
};

/**
 * Track screen view
 * @param {string} screenName - Name of the screen
 */
export const trackScreenView = (screenName) => {
  const tracker = getSessionTracker();
  tracker.trackScreen(screenName);
};

/**
 * Track product view
 * @param {string} modelId - Vehicle model ID
 */
export const trackProductView = (modelId) => {
  const tracker = getSessionTracker();
  tracker.trackProduct(modelId);
};

/**
 * Start tracking time on a page
 * @param {string} pageName - Name of the page
 */
export const startPageTracking = (pageName) => {
  const tracker = getSessionTracker();
  tracker.startPageTracking(pageName);
};

/**
 * Get engagement level as a string
 * @returns {string} Engagement level
 */
export const getEngagementLevel = () => {
  const tracker = getSessionTracker();

  if (tracker.isHighEngagement()) return 'high';

  const duration = tracker.getSessionDurationSeconds();
  const eventCount = tracker.eventCount;

  if (duration >= 60 || eventCount >= 10) return 'medium';

  return 'low';
};

/**
 * Get intent signals from current session
 * @returns {Object} Intent signals
 */
export const getIntentSignals = () => {
  const tracker = getSessionTracker();

  return {
    products_viewed_this_session: tracker.productsViewed.size,
    screens_viewed_this_session: tracker.screensViewed.size,
    session_engagement_level: getEngagementLevel(),
    is_high_intent_session: tracker.inferSessionType() === 'high_intent',
  };
};

/**
 * Reset session tracking
 * Call this on logout or session end
 */
export const resetSession = () => {
  const tracker = getSessionTracker();
  tracker.reset();
};

/**
 * Export the SessionTracker class for advanced usage
 */
export { SessionTracker };
